/**
 * Чистая логика проверки allowlist для redirect origin без чтения env.
 *
 * @module utils/surveyRedirectAllowlistCore
 */

/**
 * Проверяет, что в hostname шаблона `*` встречается ровно один раз и только в виде `*.` в начале.
 *
 * @param {string} hostname - host из URL шаблона
 * @returns {boolean} true, если wildcard-шаблон допустим
 */
function isValidWildcardHostname(hostname: string): boolean {
  if (!hostname.includes('*')) {
    return true;
  }
  if (!hostname.startsWith('*.') || hostname.slice(1).includes('*')) {
    return false;
  }
  const rest = hostname.slice(2);
  if (rest === '' || rest.includes('*')) {
    return false;
  }
  return true;
}

/**
 * Сопоставляет hostname кандидата с wildcard-шаблоном уровня `*.domain`.
 *
 * @param {string} candidateHost - host целевого origin
 * @param {string} domainSuffix - часть после `*.`
 * @returns {boolean} true, если совпадает ровно один уровень поддомена
 */
function hostMatchesSingleSubdomainWildcard(candidateHost: string, domainSuffix: string): boolean {
  if (candidateHost === domainSuffix) {
    return false;
  }
  if (!candidateHost.endsWith(`.${domainSuffix}`)) {
    return false;
  }
  const prefix = candidateHost.slice(0, -(domainSuffix.length + 1));
  if (prefix === '' || prefix.includes('.')) {
    return false;
  }
  return true;
}

/**
 * Проверяет, что origin целевого `redirect_uri` разрешён хотя бы одним элементом allowlist.
 *
 * @param {string} candidateOrigin - `URL.origin` цели (http/https)
 * @param {string[]} allowlistEntries - сырые строки из env
 * @returns {boolean} true, если candidateOrigin разрешён
 */
export function isRedirectOriginAllowed(candidateOrigin: string, allowlistEntries: string[]): boolean {
  if (candidateOrigin === '' || allowlistEntries.length === 0) {
    return false;
  }

  let candidateUrl: URL;
  try {
    candidateUrl = new URL(candidateOrigin);
  } catch {
    return false;
  }

  if (candidateUrl.protocol !== 'http:' && candidateUrl.protocol !== 'https:') {
    return false;
  }

  const candidateProto = candidateUrl.protocol;
  const candidateHost = candidateUrl.hostname;
  const candidatePort = candidateUrl.port;

  for (const entry of allowlistEntries) {
    if (entry === '') {
      continue;
    }

    let patternUrl: URL;
    try {
      patternUrl = new URL(entry);
    } catch {
      continue;
    }

    if (!isValidWildcardHostname(patternUrl.hostname)) {
      continue;
    }

    if (patternUrl.protocol !== 'http:' && patternUrl.protocol !== 'https:') {
      continue;
    }

    const patternHost = patternUrl.hostname;

    if (!patternHost.includes('*')) {
      if (patternUrl.origin === candidateUrl.origin) {
        return true;
      }
      continue;
    }

    const suffix = patternHost.slice(2);
    if (patternUrl.protocol !== candidateProto) {
      continue;
    }
    const patternPort = patternUrl.port;
    if (patternPort !== candidatePort) {
      continue;
    }
    if (hostMatchesSingleSubdomainWildcard(candidateHost, suffix)) {
      return true;
    }
  }

  return false;
}
